import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import NavBar from "./navbar/navbar";
import LoadingSpinner from "./LoadingSpinner";

// Import route modules (these are small functions, keep them eager)
import KidsOnBikesRoutes from "./routing/kids-on-bikes-routes";
import ForbiddenLandsRoutes from "./routing/forbidden-lands-routes";
import BladesInTheDarkRoutes from "./routing/blades-in-the-dark-routes";
import BladerunnerRoutes from "./routing/blade-runner-routes";
import SubstratumProtocolRoutes from "./routing/substratum-protocol-routes";

// Import auth components (small and critical, keep them eager)
import Login from "./auth/Login";
import Signup from "./auth/Signup";
import ForgotPassword from "./auth/ForgotPassword";
import VerifyEmail from "./auth/VerifyEmail";
import Dashboard from "./auth/Dashboard";
import ProtectedRoute from "./auth/ProtectedRoute";

// Lazy load all heavy components
const HomePage = lazy(() => import("./HomePage"));
const IFrameComponent = lazy(() => import("./IFrameComponent"));
const GameRundown = lazy(() => import("./GameRundown"));
const OnomasticonPage = lazy(() => import("./OnomasticonPage"));

// Lazy load Forbidden Lands components
const FLBestiaryTokens = lazy(() => import("./FLBestiaryTokens"));
const FLFindsPreciousCarried = lazy(() => import("./FLFindsPreciousCarried"));
const FLFindsPreciousLair = lazy(() => import("./FLFindsPreciousLair"));
const FLFindsSimpleCarried = lazy(() => import("./FLFindsSimpleCarried"));
const FLBestiaryTokensBitterReach = lazy(
  () => import("./FLBestiaryTokensBitterReach"),
);

// Lazy load Blade Runner components
const BRClasses = lazy(() => import("./blade-runner/BRClasses"));
const BRRules = lazy(() => import("./blade-runner/BRRules"));
const BROverview = lazy(() => import("./blade-runner/BROverview"));

// Lazy load MYZ utility components
const MYZPopulationNotableEvents = lazy(
  () => import("./myz/MYZAnchortownPopulationNotableEvents"),
);
const MYZScenes = lazy(() => import("./myz/MYZScenes"));
const MYZHandwaveTravel = lazy(() => import("./myz/MYZHandwaveTravel"));
const MYZErrata = lazy(() => import("./myz/MYZErrata"));
const MYZSectorGenerator = lazy(() => import("./myz/MYZSectorGenerator"));
const MYZGraveyard = lazy(() => import("./myz/MYZAnchortownGraveyard"));
const MYZMap = lazy(() => import("./myz/MYZMap"));
const MYZMapGridExploration = lazy(() => import("./myz/MYZMapGridExploration"));
const MYZCoverGallery = lazy(() => import("./myz/MYZCoverGallery"));

// Lazy load MYZ session components
const MYZSession01 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession01"),
);
const MYZSession02 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession02"),
);
const MYZSession03 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession03"),
);
const MYZSession04 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession04"),
);
const MYZSession05 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession05"),
);
const MYZSession06 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession06"),
);
const MYZSession07 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession07"),
);
const MYZSession08 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession08"),
);
const MYZSession09 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession09"),
);
const MYZSession10 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession10"),
);
const MYZSession11 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession11"),
);
const MYZSession12 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession12"),
);
const MYZSession13 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession13"),
);
const MYZSession14 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession14"),
);
const MYZSession15 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession15"),
);
const MYZSession16 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession16"),
);
const MYZSession17 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession17"),
);
const MYZAnchortownSession18And19And20 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession18And19And20"),
);
const MYZAnchortownSession21 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession21"),
);
const MYZAnchortownSession22 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession22"),
);
const MYZAnchortownSession23 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession23"),
);
const MYZAnchortownSession24 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession24"),
);
const MYZAnchortownSession25 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession25"),
);
const MYZAnchortownSession26 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession26"),
);
const MYZAnchortownSession27 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession27"),
);
const MYZAnchortownSession28 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession28"),
);
const MYZAnchortownSession29 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession29"),
);
const MYZAnchortownSession30 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession30"),
);
const MYZAnchortownSession31 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession31"),
);
const MYZAnchortownSession32 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession32"),
);
const MYZAnchortownSession33 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession33"),
);
const MYZAnchortownSession34 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession34"),
);
const MYZAnchortownSession35 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession35"),
);
const MYZAnchortownSession36 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession36"),
);
const MYZAnchortownSession37 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession37"),
);
const MYZAnchortownSession38 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession38"),
);
const MYZAnchortownSession39 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession39"),
);
const MYZAnchortownSession40 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession40"),
);
const MYZAnchortownSession41 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession41"),
);
const MYZAnchortownSession42 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession42"),
);
const MYZAnchortownSession43And44 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession43And44"),
);
const MYZAnchortownSession45 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession45"),
);
const MYZAnchortownSession46 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession46"),
);
const MYZAnchortownSession47 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession47"),
);
const MYZAnchortownSession48 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession48"),
);
const MYZAnchortownSession49 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession49"),
);
const MYZAnchortownSession50 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession50"),
);
const MYZAnchortownSession51 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession51"),
);
const MYZAnchortownSession52 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession52"),
);
const MYZAnchortownSession53 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession53"),
);
const MYZAnchortownSession54 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession54"),
);
const MYZAnchortownSession55 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession55"),
);
const MYZAnchortownSession56And57 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession56And57"),
);
const MYZAnchortownSession58And60 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession58And60"),
);
const MYZAnchortownSession61And62 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession61And62"),
);
const MYZAnchortownSession63 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession63"),
);
const MYZAnchortownSession64And65 = lazy(
  () => import("./myz/sessions/MYZAnchortownSession64And65"),
);

// Lazy load Mork Borg components
const MBMalumMortisSession01 = lazy(
  () => import("./morkborg/MBMalumMortisSession01"),
);
const MBMalumMortisSession02 = lazy(
  () => import("./morkborg/MBMalumMortisSession02"),
);
const MBMalumMortisSession03 = lazy(
  () => import("./morkborg/MBMalumMortisSession03"),
);
const MBMalumMortisSession04 = lazy(
  () => import("./morkborg/MBMalumMortisSession04"),
);
const MBMalumMortisSession05 = lazy(
  () => import("./morkborg/MBMalumMortisSession05"),
);
const MBMalumMortisSession06 = lazy(
  () => import("./morkborg/MBMalumMortisSession06"),
);
const MBMalumMortisSession07 = lazy(
  () => import("./morkborg/MBMalumMortisSession07"),
);
const MBMalumMortisSession08 = lazy(
  () => import("./morkborg/MBMalumMortisSession08"),
);
const MBMalumMortisSession09 = lazy(
  () => import("./morkborg/MBMalumMortisSession09"),
);
const MBMalumMortisSession10 = lazy(
  () => import("./morkborg/MBMalumMortisSession10"),
);
const MBMalumMortisSession11 = lazy(
  () => import("./morkborg/MBMalumMortisSession11"),
);
const MBGraveyard = lazy(() => import("./morkborg/MBMalumMortisGraveyard"));
const MBExquisiteCorpsesSession00 = lazy(
  () => import("./morkborg/MBExquisiteCorpsesSession00"),
);
const MBExquisiteCorpsesSession01 = lazy(
  () => import("./morkborg/MBExquisiteCorpsesSession01"),
);
const MBClassesCore = lazy(() => import("./morkborg/MBClassesCore"));
const MBClassesThirdParty = lazy(
  () => import("./morkborg/MBClassesThirdParty"),
);
const MBClassesGallery = lazy(() => import("./morkborg/MBClassesGallery"));

class SmokeRavenNavbar extends React.Component {
  render() {
    return (
      <div>
        <div className="row">
          <div className="col-md-12">
            <Router>
              <NavBar />
              <br />
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/onomasticon" element={<OnomasticonPage />} />
                  {ForbiddenLandsRoutes()}
                  {BladesInTheDarkRoutes()}
                  {SubstratumProtocolRoutes()}
                  {KidsOnBikesRoutes()}
                  {BladerunnerRoutes()}
                  <Route
                    path="/scrap-generator"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/myz-scrap.html"
                        }
                        creditName="jjpeabody"
                        creditUrl={
                          "https://www.deviantart.com/jjpeabody/gallery"
                        }
                      />
                    }
                  />
                  <Route
                    path="/mutant-generator"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/myz-mutant.html"
                        }
                        creditName=""
                        creditUrl={
                          "https://www.deviantart.com/everlite/art/Ruincityscape-001-2k-541701995"
                        }
                      />
                    }
                  />
                  <Route
                    path="/mutant-year-zero-scrap-rules"
                    element={
                      <IFrameComponent targetUrl="https://www.smokeraven.com/static/html/mutant-year-zero-scrap-rules.html" />
                    }
                  />
                  <Route
                    path="/mutant-year-zero-civil-war-rules"
                    element={
                      <IFrameComponent targetUrl="https://www.smokeraven.com/static/html/mutant-year-zero-civil-war-rules.htm" />
                    }
                  />
                  <Route
                    path="/mutant-year-zero-beast-training"
                    element={
                      <IFrameComponent targetUrl="https://www.smokeraven.com/static/html/mutant-year-zero-beast-training.html" />
                    }
                  />
                  <Route
                    path="/mutant-year-zero-rot-critical-injuries"
                    element={
                      <IFrameComponent targetUrl="https://www.smokeraven.com/static/html/CriticalInjuriesRot.html" />
                    }
                  />
                  <Route
                    path="/mutant-year-zero-anchortown-population-notable-events"
                    element={<MYZPopulationNotableEvents />}
                  />
                  <Route
                    path="/mutant-year-zero-scenes"
                    element={<MYZScenes />}
                  />
                  <Route
                    path="/mutant-year-zero-handwave-travel-rules"
                    element={<MYZHandwaveTravel />}
                  />
                  <Route
                    path="/mutant-year-zero-errata"
                    element={<MYZErrata />}
                  />
                  <Route
                    path="/mutant-year-zero-sector-generator"
                    element={<MYZSectorGenerator />}
                  />
                  <Route
                    path="/mutant-year-zero-session-01-anchortown"
                    element={<MYZSession01 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-02-anchortown"
                    element={<MYZSession02 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-03-anchortown"
                    element={<MYZSession03 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-04-anchortown"
                    element={<MYZSession04 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-05-anchortown"
                    element={<MYZSession05 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-06-anchortown"
                    element={<MYZSession06 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-07-anchortown"
                    element={<MYZSession07 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-08-anchortown"
                    element={<MYZSession08 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-09-anchortown"
                    element={<MYZSession09 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-10-anchortown"
                    element={<MYZSession10 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-11-anchortown"
                    element={<MYZSession11 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-12-anchortown"
                    element={<MYZSession12 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-13-anchortown"
                    element={<MYZSession13 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-14-anchortown"
                    element={<MYZSession14 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-15-anchortown"
                    element={<MYZSession15 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-16-anchortown"
                    element={<MYZSession16 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-17-anchortown"
                    element={<MYZSession17 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-18-19-20-anchortown"
                    element={<MYZAnchortownSession18And19And20 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-21-anchortown"
                    element={<MYZAnchortownSession21 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-22-anchortown"
                    element={<MYZAnchortownSession22 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-23-anchortown"
                    element={<MYZAnchortownSession23 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-24-anchortown"
                    element={<MYZAnchortownSession24 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-25-anchortown"
                    element={<MYZAnchortownSession25 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-26-anchortown"
                    element={<MYZAnchortownSession26 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-27-anchortown"
                    element={<MYZAnchortownSession27 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-28-anchortown"
                    element={<MYZAnchortownSession28 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-29-anchortown"
                    element={<MYZAnchortownSession29 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-30-anchortown"
                    element={<MYZAnchortownSession30 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-31-anchortown"
                    element={<MYZAnchortownSession31 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-32-anchortown"
                    element={<MYZAnchortownSession32 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-33-anchortown"
                    element={<MYZAnchortownSession33 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-34-anchortown"
                    element={<MYZAnchortownSession34 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-35-anchortown"
                    element={<MYZAnchortownSession35 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-36-anchortown"
                    element={<MYZAnchortownSession36 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-37-anchortown"
                    element={<MYZAnchortownSession37 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-38-anchortown"
                    element={<MYZAnchortownSession38 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-39-anchortown"
                    element={<MYZAnchortownSession39 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-40-anchortown"
                    element={<MYZAnchortownSession40 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-41-anchortown"
                    element={<MYZAnchortownSession41 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-42-anchortown"
                    element={<MYZAnchortownSession42 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-43_44-anchortown"
                    element={<MYZAnchortownSession43And44 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-45-anchortown"
                    element={<MYZAnchortownSession45 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-46-anchortown"
                    element={<MYZAnchortownSession46 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-47-anchortown"
                    element={<MYZAnchortownSession47 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-48-anchortown"
                    element={<MYZAnchortownSession48 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-49-anchortown"
                    element={<MYZAnchortownSession49 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-50-anchortown"
                    element={<MYZAnchortownSession50 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-51-anchortown"
                    element={<MYZAnchortownSession51 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-52-anchortown"
                    element={<MYZAnchortownSession52 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-53-anchortown"
                    element={<MYZAnchortownSession53 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-54-anchortown"
                    element={<MYZAnchortownSession54 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-55-anchortown"
                    element={<MYZAnchortownSession55 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-56-57-anchortown"
                    element={<MYZAnchortownSession56And57 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-58-60-anchortown"
                    element={<MYZAnchortownSession58And60 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-61-62-anchortown"
                    element={<MYZAnchortownSession61And62 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-63-anchortown"
                    element={<MYZAnchortownSession63 />}
                  />
                  <Route
                    path="/mutant-year-zero-session-64_65-anchortown"
                    element={<MYZAnchortownSession64And65 />}
                  />
                  <Route
                    path="/mutant-year-zero-anchortown-graveyard"
                    element={<MYZGraveyard />}
                  />
                  <Route
                    path="/mutant-year-zero-anchortown-map"
                    element={<MYZMap />}
                  />
                  <Route
                    path="/mutant-year-zero-anchortown-map-grid-exploration"
                    element={<MYZMapGridExploration />}
                  />
                  <Route
                    path="/mutant-year-zero-cover-gallery"
                    element={<MYZCoverGallery />}
                  />
                  <Route
                    path="/mork-borg-session-01-malum-mortis"
                    element={<MBMalumMortisSession01 />}
                  />
                  <Route
                    path="/mork-borg-session-02-malum-mortis"
                    element={<MBMalumMortisSession02 />}
                  />
                  <Route
                    path="/mork-borg-session-03-malum-mortis"
                    element={<MBMalumMortisSession03 />}
                  />
                  <Route
                    path="/mork-borg-session-04-malum-mortis"
                    element={<MBMalumMortisSession04 />}
                  />
                  <Route
                    path="/mork-borg-session-05-malum-mortis"
                    element={<MBMalumMortisSession05 />}
                  />
                  <Route
                    path="/mork-borg-session-06-malum-mortis"
                    element={<MBMalumMortisSession06 />}
                  />
                  <Route
                    path="/mork-borg-session-07-malum-mortis"
                    element={<MBMalumMortisSession07 />}
                  />
                  <Route
                    path="/mork-borg-session-08-malum-mortis"
                    element={<MBMalumMortisSession08 />}
                  />
                  <Route
                    path="/mork-borg-session-09-malum-mortis"
                    element={<MBMalumMortisSession09 />}
                  />
                  <Route
                    path="/mork-borg-session-10-malum-mortis"
                    element={<MBMalumMortisSession10 />}
                  />
                  <Route
                    path="/mork-borg-session-11-malum-mortis"
                    element={<MBMalumMortisSession11 />}
                  />
                  <Route
                    path="/mork-borg-malum-mortis-graveyard"
                    element={<MBGraveyard />}
                  />
                  <Route
                    path="/mork-borg-session-01-exquisite-corpses"
                    element={<MBExquisiteCorpsesSession01 />}
                  />
                  <Route
                    path="/mork-borg-session-00-exquisite-corpses"
                    element={<MBExquisiteCorpsesSession00 />}
                  />
                  <Route
                    path="/mork-borg-classes-core"
                    element={<MBClassesCore />}
                  />

                  <Route
                    path="/mork-borg-classes-third-party"
                    element={<MBClassesThirdParty />}
                  />
                  <Route
                    path="/mork-borg-classes-gallery"
                    element={<MBClassesGallery />}
                  />
                  <Route path="/game-rundown" element={<GameRundown />} />

                  <Route
                    path="/forbidden-lands-bestiary-roll20-tokens"
                    element={<FLBestiaryTokens />}
                  />
                  <Route
                    path="/forbidden-lands-finds-precious-carried"
                    element={<FLFindsPreciousCarried />}
                  />
                  <Route
                    path="/forbidden-lands-finds-precious-lair"
                    element={<FLFindsPreciousLair />}
                  />
                  <Route
                    path="/forbidden-lands-finds-simple-carried"
                    element={<FLFindsSimpleCarried />}
                  />
                  <Route
                    path="/forbidden-lands-bitter-reach-bestiary-roll20-tokens"
                    element={<FLBestiaryTokensBitterReach />}
                  />
                  <Route
                    path="/forbidden-lands-legend-generator"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-legend-generator.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-magic-mishaps"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-magic-mishaps.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-lead-the-way-mishaps"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-lead-the-way-mishaps.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-foraging-mishaps"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-foraging-mishaps.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-hunting-mishaps"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-hunting-mishaps.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-fishing-mishaps"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-fishing-mishaps.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-making-camp-mishaps"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-making-camp-mishaps.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-sea-travel-mishaps"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-sea-travel-mishaps.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-stronghold-generator"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-stronghold-generator.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-critical-injuries"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-critical-injuries.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-stronghold-events"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-stronghold-events.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/ark-projects-summary"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://docs.google.com/spreadsheets/d/1I_XrbBH7LMGt7QinlHomINAIM2es-vochY3378yUYXE/"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-demon-generator"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-generate-demon.html"
                        }
                      />
                    }
                  />
                  <Route
                    path="/forbidden-lands-adventurer-generator"
                    element={
                      <IFrameComponent
                        targetUrl={
                          "https://www.smokeraven.com/static/html/forbidden-lands-adventurer-generator.html"
                        }
                      />
                    }
                  />
                  {/* <Route path="/forbidden-lands-lead-the-way" element={<IFrameComponent targetUrl={"https://www.smokeraven.com/static/html/forbidden-lands-lead-the-way.html"}/>}/> */}
                  <Route
                    path="/blade-runner-overview"
                    element={<BROverview />}
                  />
                  <Route path="/blade-runner-classes" element={<BRClasses />} />
                  <Route path="/blade-runner-rules" element={<BRRules />} />

                  {/* Auth routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </Router>
          </div>
        </div>
      </div>
    );
  }
}

export default SmokeRavenNavbar;
